import React, { useEffect, useState } from "react";
import ProductDetails from "./ProductDetails";
import {
  createEmptyFileData,
  createEmptyProductDetails,
} from "../../services/Application";
import FileUploadComponent from "./FileUploadComponent";
import ValidationInfo from "./ValidationInfo";
import { validateLettersDigitsSpecChars } from "../../utils/Validators";
import HelpTooltip from "./HelpTooltip";
import ReadonlyOverlay from "./ReadonlyOverlay";
import LoadingSpinner from "./LoadingSpinner";
import PreviousValue from "./PreviousValue";
import CommunicationSpot from "./CommunicationSpot";
import ConfirmPopup from "./ConfirmPopup";
import ProductMultiSelector from "./ProductMultiSelector";

const ProductionProcess = ({
  index,
  name,
  data,
  previousData = {},
  showPreviousData = false,
  productsList,
  onDataChange,
  onRemove,
  commentVisible = true,
  registerCallback,
  viewAll = false,
  handleValidate,
  readOnly = false,
}) => {
  const [processData, setProcessData] = useState(data);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [removedProducts, setRemovedProducts] = useState("");
  const [availableProducts, setAvailableProducts] = useState([]);
  const [processFiles, setProcessFiles] = useState([createEmptyFileData()]);
  const [productionProcessDescription, setProductionProcessDescription] =
    useState(data.productionProcessDescription);
  const [validators, setValidators] = useState({
    productionProcessDescription: "",
    tariffCode: "",
  });
  const [loading, setLoading] = useState(true);
  const [fileDifferences, setFileDifferences] = useState(null);
  const [productDifferences, setProductDifferences] = useState(null);
  const tooltips = [
    "Proces produkcji każdego z produktów musi być opisany. Jeżeli nie masz dokumentu opisującego proces produkcji (w tym koniecznie kraj pochodzenia, kod taryfy celnej), musisz wypełnić pola poniżej. Jeżeli masz taki dokument, załącz go.",
  ];
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmDeleteProduct, setConfirmDeleteProduct] = useState(false);
  const [confirmDeleteAllProducts, setConfirmDeleteAllProducts] =
    useState(false);
  const [confirmDeleteProductName, setConfirmDeleteProductName] = useState("");

  // Funkcje zwrotne do aktualizacji stanu komponentów
  const handleProductionProcessDescriptionChange = (e) => {
    setProductionProcessDescription(e.target.value);
    onDataChange({
      target: {
        name: name,
        value: {
          id: data.id,
          productionProcessDescription: e.target.value,
          processFiles: processFiles,
          Products: processData.Products,
        },
        index: index,
      },
    });
    setValidators({
      ...validators,
      productionProcessDescription: validateLettersDigitsSpecChars(
        e.target.value,
        1000,
        processFiles[0].name === ""
      )
        ? "false"
        : "true",
    });
  };

  const setValidator = (name, validator) => {
    setValidators((prevValidators) => {
      return {
        ...prevValidators,
        [name]: validator,
      };
    });
  };

  const removeValidator = (name) => {
    setValidators((prevValidators) => {
      const updatedValidators = { ...prevValidators };
      delete updatedValidators[name];
      return updatedValidators;
    });
  };

  const handleProductionProcessFilesChange = () => {
    onDataChange({
      target: {
        name: name,
        value: {
          id: data.id,
          productionProcessDescription: productionProcessDescription,
          processFiles: processFiles,
          Products: processData.Products,
        },
        index: index,
      },
    });
  };

  const handleFileAdd = (fileType) => {
    switch (fileType) {
      case "processFiles":
        setProcessFiles((prevFiles) => [...prevFiles, createEmptyFileData()]);
        break;
      default:
        break;
    }
  };

  const handleFileChange = (
    id,
    name,
    description,
    fileType,
    index,
    newFile = true
  ) => {
    const fileInfo = createEmptyFileData();
    fileInfo.id = id;
    fileInfo.name = name;
    fileInfo.description = description;

    switch (fileType) {
      case "processFiles":
        setProcessFiles((prevFiles) => {
          const updatedFiles = [...prevFiles];
          updatedFiles[index] = fileInfo;
          if (newFile) updatedFiles.push(createEmptyFileData());
          return updatedFiles;
        });
        break;
      default:
    }
  };

  const handleFileRemove = (fileType, index) => {
    // console.log("PP hFR: " + fileType + ", " + index);
    switch (fileType) {
      case "processFiles":
        setProcessFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
        break;
      default:
        break;
    }
  };

  const handleAddProductDetails = (newProductName = null) => {
    setProcessData((prevProcessData) => {
      const newProduct = createEmptyProductDetails();
      newProduct.name = newProductName || selectedProduct;
      const updatedProducts = [...prevProcessData.Products, newProduct];
      onDataChange({
        target: {
          name: name,
          value: { ...prevProcessData, Products: updatedProducts },
          index: index,
        },
      });
      return { ...prevProcessData, Products: updatedProducts };
    });
    setAvailableProducts(
      availableProducts.filter(
        (prod) => prod.productName !== (newProductName || selectedProduct)
      )
    );
    setSelectedProduct("");
  };

  const handleDeleteProductDetails = (productName) => {
    setAvailableProducts([...availableProducts, { productName: productName }]);
    setProcessData((prevProcessData) => {
      const updatedProducts = prevProcessData.Products.filter(
        (product) => product.name !== productName
      );
      onDataChange({
        target: {
          name: name,
          value: { ...prevProcessData, Products: updatedProducts },
          index: index,
        },
      });
      return { ...prevProcessData, Products: updatedProducts };
    });
  };

  const handleDeleteAllProductsDetails = () => {
    setAvailableProducts([
      ...availableProducts,
      ...processData.Products.map((product) => ({ productName: product.name })),
    ]);
    setProcessData((prevProcessData) => {
      onDataChange({
        target: {
          name: name,
          value: { ...prevProcessData, Products: [] },
          index: index,
        },
      });
      return { ...prevProcessData, Products: [] };
    });
  };

  const handleUpdateProductDetails = (productDetails, pdIndex) => {
    setProcessData((prevProcessData) => {
      const updatedProducts = [...prevProcessData.Products];
      updatedProducts[pdIndex] = productDetails;
      onDataChange({
        target: {
          name: name,
          value: { ...prevProcessData, Products: updatedProducts },
          index: index,
        },
      });
      return { ...prevProcessData, Products: updatedProducts };
    });
  };

  const handleRemoveProductDetails = (pIndex) => {
    setAvailableProducts([
      ...availableProducts,
      { productName: processData.Products[pIndex].name },
    ]);
    setRemovedProducts((prevIndexes) => [...prevIndexes, pIndex]);
    removeValidator("productDetails_" + index + "_" + pIndex);
  };

  useEffect(() => {
    const filteredProducts = processData.Products.filter(
      (_, index) => !removedProducts.includes(index)
    );
    const updatedData = { ...processData, Products: filteredProducts };
    onDataChange({ target: { name: name, value: updatedData, index: index } });
  }, [removedProducts]);

  useEffect(() => {
    if (processFiles.length > 0) {
      handleProductionProcessFilesChange();
    }
    setValidators({
      ...validators,
      productionProcessDescription: validateLettersDigitsSpecChars(
        productionProcessDescription,
        1000,
        processFiles[0].name === ""
      )
        ? "false"
        : "true",
    });
  }, [processFiles]);

  const compareProducts = async () => {
    let differences = [];
    let differencesSummary = "";
    if (previousData.Products) {
      differences = previousData.Products.filter(
        (prevProduct) =>
          !processData.Products.some((product) => product.id === prevProduct.id)
      );
    }
    if (differences.length > 0)
      differencesSummary =
        "<strong>Usunięte produkty:</strong><br/>" +
        differences.map((product) => "- " + product.name).join(",<br/>");

    if (previousData.Products && previousData.Products.length > 0)
      differences = processData.Products.filter(
        (product) =>
          !previousData.Products.some(
            (prevProduct) => prevProduct.id === product.id
          )
      );
    if (differences.length > 0)
      differencesSummary +=
        (differencesSummary.length > 0 ? "<br/>" : "") +
        "<strong>Dodane produkty:</strong><br/>" +
        differences.map((product) => "- " + product.name).join(",<br/>");

    setProductDifferences(differencesSummary);
  };

  const compareFiles = async () => {
    //sprawdzanie różnic w plikach w wersji edytowanej i poprzedniej - usunięte pliki z poprzedniej wersji
    let differences = [];
    let differencesSummary = "";
    if (previousData.processFiles) {
      differences = previousData.processFiles.filter(
        (prevFile) => !data.processFiles.some((file) => file.id === prevFile.id)
      );
    }
    if (differences.length > 0)
      differencesSummary =
        "<strong>Usunięte pliki:</strong><br/>" +
        differences.map((file) => "- " + file.name).join(",<br/>");

    //sprawdzanie różnic w plikach w wersji edytowanej i poprzedniej - nowe pliki w wersji edytowanej
    if (previousData.processFiles && previousData.processFiles.length > 0)
      differences = data.processFiles.filter(
        (file) =>
          !previousData.processFiles.some((prevFile) => prevFile.id === file.id)
      );
    if (differences.length > 0)
      differencesSummary +=
        (differencesSummary.length > 0 ? "<br/>" : "") +
        "<strong>Dodane pliki:</strong><br/>" +
        differences.map((file) => "- " + file.name).join(",<br/>");

    setFileDifferences(differencesSummary);
  };

  const findMatchingPreviousProductDetails = (product) => {
    if (previousData.Products) {
      const matchingProduct = previousData.Products.find(
        (prevProduct) => prevProduct.id === product.id
      );
      if (matchingProduct) {
        return matchingProduct;
      }
    }
    return {};
  };

  useEffect(() => {
    const excludedNames = processData.Products.map((product) => product.name);

    let filteredProducts = productsList.filter(
      (product) => !excludedNames.includes(product.productName)
    );

    setAvailableProducts(filteredProducts);
  }, [productsList, processData.Products]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      if (data.processFiles) setProcessFiles(data.processFiles);

      await compareFiles();
      await compareProducts();
      setLoading(false);
    };

    loadData();
  }, []);

  useEffect(() => {
    let count = 0;
    for (const key in validators) {
      if (
        validators.hasOwnProperty(key) &&
        (validators[key] === "true" || validators[key] === true)
      ) {
        count++;
      }
    }
    handleValidate("Process_" + data.id, count === 0 ? "false" : "true");
  }, [validators]);

  useEffect(() => {
    if (confirmDeleteProductName !== "") setConfirmDeleteProduct(true);
    else setConfirmDeleteProduct(false);
  }, [confirmDeleteProductName]);

  const handleConfirmProductSelection = (changes) => {
    const scrollPosition = window.pageYOffset;

    setProcessData((prevProcessData) => {
      let updatedProducts = [...prevProcessData.Products];

      if (changes.toRemove.length > 0) {
        updatedProducts = updatedProducts.filter(
          (product) => !changes.toRemove.includes(product.name)
        );
      }

      changes.toAdd.forEach((productName) => {
        const newProduct = createEmptyProductDetails();
        newProduct.name = productName;
        updatedProducts.push(newProduct);
      });

      onDataChange({
        target: {
          name: name,
          value: { ...prevProcessData, Products: updatedProducts },
          index: index,
        },
      });

      return { ...prevProcessData, Products: updatedProducts };
    });

    setAvailableProducts((prevAvailable) => {
      let updated = [...prevAvailable];
      changes.toRemove.forEach((productName) => {
        updated.push({ productName });
      });
      updated = updated.filter(
        (prod) => !changes.toAdd.includes(prod.productName)
      );
      return updated;
    });

    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 0);
  };

  return (
    <div className="card container-fluid shadow ms-4 my-2 py-3 px-0 w55 fade fade-in">
      <div>
        <div className="d-flex flex-row px-4 pb-2 w-100 border-bottom fs-4">
          <div>
            <strong>{"Proces produkcji nr " + (index + 1)}</strong>
          </div>

          {!readOnly && (
            <div className="ms-auto fs-5">
              <button
                type="button"
                key={"ProcessRemove_" + index}
                className="btn btn-outline-primary rounded-2 w7 h2 ms-auto mb-0"
                onClick={() => setConfirmDelete(true)}
              >
                Usuń proces
              </button>
              <ConfirmPopup
                show={confirmDelete}
                title="Potwierdź usunięcie procesu"
                text="Potwierdź usunięcie procesu produkcji. Operacji nie można cofnąć."
                onConfirm={() => {
                  onRemove(data.id, index);
                  setConfirmDelete(false);
                }}
                onCancel={() => setConfirmDelete(false)}
              />
            </div>
          )}
        </div>
      </div>

      <div className="d-flex flex-column px-4">
        <ReadonlyOverlay readonly={readOnly}>
          <div className="d-flex flex-column pt-2 pb-2 mb-2 border-bottom">
            <div className="d-flex flex-row">
              <label className="form-label">
                Wybierz wszystkie produkty posiadające taki sam proces produkcji
                *
              </label>
              <HelpTooltip
                title="Jeżeli któryś produkt ma inny proces produkcji, dodaj kolejny proces produkcji."
                wide="true"
              />
            </div>
            <ProductMultiSelector
              index={index}
              selectedProducts={processData.Products.map(
                (product) => product.name
              )}
              availableProducts={availableProducts.map(
                (product) => product.productName
              )}
              onSelect={handleAddProductDetails}
              onRemove={setConfirmDeleteProductName}
              onRemoveAll={() => setConfirmDeleteAllProducts(true)}
              openAtStart={processData.Products.length === 0}
              readOnly={readOnly}
              onConfirm={handleConfirmProductSelection}
            />

            <ConfirmPopup
              show={confirmDeleteProduct}
              title="Potwierdź usunięcie produktu"
              text={`Potwierdź usunięcie produktu: ${confirmDeleteProductName}. Operacji nie można cofnąć.`}
              onConfirm={() => {
                handleDeleteProductDetails(confirmDeleteProductName);
                setConfirmDeleteProduct(false);
                setConfirmDeleteProductName("");
              }}
              onCancel={() => {
                setConfirmDeleteProduct(false);
                setConfirmDeleteProductName("");
              }}
            />

            <ConfirmPopup
              show={confirmDeleteAllProducts}
              title="Potwierdź usunięcie produktów"
              text={`Potwierdź usunięcie wszystkich produktów z procesu produkcji. Operacji nie można cofnąć.`}
              onConfirm={() => {
                handleDeleteAllProductsDetails();
                setConfirmDeleteAllProducts(false);
              }}
              onCancel={() => {
                setConfirmDeleteAllProducts(false);
              }}
            />
          </div>

          <div className="d-flex flex-column mb-1">
            <div className="d-flex flex-row">
              <h4>Opis procesu produkcji *</h4>
              <HelpTooltip title={tooltips[0]} wide={true} />
            </div>
            <div>
              Wypełnij pola poniżej lub załącz dokument zawierający szczegółowy
              opis procesu produkcji.
            </div>
          </div>

          <PreviousValue
            show={showPreviousData && productDifferences}
            previousValue={productDifferences}
            header="Wprowadzone zmiany<hr/>"
          >
            {processData.Products.map((product, pdIndex) => (
              <div
                className="pt-1 pb-2 border-bottom"
                key={"ProductDetailsDiv_" + product.name}
              >
                <ProductDetails
                  index={pdIndex}
                  key={"ProductDetails_" + index + "_" + pdIndex}
                  name={"productDetails_" + index + "_" + pdIndex}
                  data={product}
                  previousData={findMatchingPreviousProductDetails(product)}
                  showPreviousData={showPreviousData}
                  onDataChange={handleUpdateProductDetails}
                  onRemove={() => setConfirmDeleteProductName(product.name)}
                  // onRemove={() => handleRemoveProductDetails(pdIndex)}
                  commentVisible={commentVisible}
                  registerCallback={registerCallback}
                  viewAll={viewAll}
                  handleValidate={setValidator}
                />
              </div>
            ))}
          </PreviousValue>
        </ReadonlyOverlay>

        <div className="mt-2">
          <div>
            <ReadonlyOverlay readonly={readOnly}>
              <div className="mb-2">
                <div className="d-flex align-items-center">
                  <label className="form-label">
                    Szczegółowy opis procesu produkcji{" "}
                  </label>
                  <ValidationInfo
                    title="Uzupełnij opis procesu produkcji (max 1000 znaków) lub dodaj załącznik z opisem."
                    visible={
                      validators.productionProcessDescription === "true"
                        ? "true"
                        : "false"
                    }
                    wide={true}
                  />
                  <CommunicationSpot
                    commentId={"3-1-" + index}
                    location={"Proces produkcji - " + index}
                    isVisible={commentVisible}
                    registerCallback={registerCallback}
                    viewAll={viewAll}
                  />
                </div>
                <PreviousValue
                  show={
                    showPreviousData &&
                    previousData.productionProcessDescription &&
                    previousData.productionProcessDescription !==
                      productionProcessDescription
                  }
                  previousValue={previousData.productionProcessDescription}
                >
                  <textarea
                    className="form-control my-0 h8 rounded-3"
                    maxLength="1000"
                    name={"Process_" + index + "_productionProcessDescription"}
                    value={productionProcessDescription}
                    onChange={handleProductionProcessDescriptionChange}
                    placeholder="Szczegółowy opis procesu produkcji"
                    invalid={
                      validators.productionProcessDescription === "true"
                        ? "true"
                        : "false"
                    }
                    onBlur={handleProductionProcessDescriptionChange}
                  />
                </PreviousValue>
              </div>

              <div className="mb-1">
                <span>
                  lub dodaj dokumenty ze szczegółowym opisem procesu produkcji
                </span>
              </div>
            </ReadonlyOverlay>

            {loading ? (
              <LoadingSpinner />
            ) : (
              <PreviousValue
                show={showPreviousData && fileDifferences}
                previousValue={fileDifferences}
                header="Wprowadzone zmiany<hr/>"
              >
                {processFiles.length > 0 &&
                  processFiles.map((file, fIndex) => (
                    <div
                      className={fIndex > 0 ? "mt-2" : "mt-0"}
                      key={"processFiles_" + index + "_" + fIndex}
                    >
                      <FileUploadComponent
                        fileId={file.id}
                        fileName={file.name}
                        fileDescription={file.description}
                        fileType="processFiles"
                        index={fIndex}
                        onChange={handleFileChange}
                        onRemove={() =>
                          handleFileRemove("processFiles", fIndex)
                        }
                        readOnly={readOnly ? "true" : "false"}
                        buttonLabel={
                          fIndex === 0 ? "Dodaj plik" : "Dodaj kolejny plik"
                        }
                      />
                    </div>
                  ))}
              </PreviousValue>
            )}

            {processFiles.length < 1 && !readOnly && (
              <div>
                <button
                  type="button"
                  className="btn btn-outline-primary w7 h2 rounded-2"
                  onClick={() => {
                    handleFileAdd("processFiles");
                  }}
                >
                  Dodaj plik
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductionProcess;
